import { useEffect, useLayoutEffect, useRef, useSyncExternalStore } from 'react';

import { secureStore } from '../platform';
import { getUserId, isTokenValid } from './index';
import { REFRESH_KEY, TOKEN_KEY, currentRefresh, currentToken, holdToken, onTokenChangedElsewhere, restoreToken, tokenChanges } from './sessionToken';

/**
 * WHO is signed in on this tab — the one signal every per-user client cache
 * resets on.
 *
 * The bug class this closes: a cache that latched on its first load (a
 * `requestedRef`, a module-level Map, a hook's state in a component that
 * stays mounted through the /logout hop, like the shell's palette) kept the
 * previous account's data when a second account signed in on the same tab —
 * their preferences, favorites, Home pins, palette rows, recent views. Each
 * cache used to guess at "login state may have changed" from route changes
 * and `isTokenValid()`; none noticed A → B, because both are signed in.
 *
 * The session is the token's `userId`, or null without a valid token. The
 * token is held IN MEMORY (utils/sessionToken.js; KPR-1669, docs/plans/
 * ios-app.md Decision 2): read once at boot from the platform's secure store
 * (localStorage on the web, the Keychain in the app, which can only answer
 * asynchronously) by `hydrateSession()`, which src/index.js awaits before
 * the first render, and changed only through setSessionToken /
 * clearSessionToken, which write it through to the store. Nothing else reads
 * or writes the stored token (src/__tests__/sessionTokenStore.test.js).
 *
 *   hydrateSession()            boot: read the stored token once, then render
 *   getSessionToken()           the raw token, for the API client and the
 *                               few readers that compare credentials
 *   useSessionUserId()          the id, re-rendering on a change announced by
 *                               setSessionToken/clearSessionToken or another
 *                               tab (the store's `subscribe`; on the web the
 *                               `storage` event)
 *   useOnSessionChange(reset)   for a context or hook: calls `reset(next,
 *                               previous)` in a layout effect when the id
 *                               changes (never on mount), before paint — so
 *                               the next account never sees a frame of the
 *                               last one's data. Returns the id; put it in the
 *                               deps of the effect that loads.
 *   onSessionChange(listener)   for a MODULE-level cache (useMe, UserRef, …):
 *                               clear it when the id changes.
 *   getSessionKey()             the stamp a module cache's entries carry, so
 *                               an entry is served only to its own session.
 *   setSessionToken / clearSessionToken
 *                               the ways the app writes the token, so the
 *                               change is announced at once rather than at
 *                               the next render that happens to read it.
 *   whenSessionKept()           resolves when those writes reached the store
 *   getRefreshCredential()      the phone app's device-session refresh
 *                               credential (KPR-1674), for the api client's
 *                               renewal only; null on the web
 *
 * Keying the providers on the user id (a remount) was the other shape; it
 * would remount the whole app under them and still miss module caches and
 * hooks outside a provider. A token refresh for the SAME account (the
 * password change) keeps the id, so nothing resets.
 */

// The token is kept under TOKEN_KEY ('token', utils/sessionToken.js) in the
// platform's secure store. A device session's refresh credential (KPR-1674,
// the phone app only) is kept beside it under REFRESH_KEY, written by the
// same two writers below and read by the same boot.

// How long the first render waits on a store that does not answer. The web's
// answers in a microtask; a Keychain that hangs must not leave a blank app.
// After this the app renders signed out, and a late answer is still taken
// (below) unless the session changed meanwhile.
export const HYDRATE_TIMEOUT_MS = 5000;

/** The token this tab holds, or null. The API client sends it as the bearer. */
export function getSessionToken() {
	return currentToken();
}

/**
 * The device session's refresh credential (`kpd_…`), or null: the api
 * client's renewal (src/api/client.js) is its only reader. Only the phone
 * app's sign-in answers one; the web never holds it.
 */
export function getRefreshCredential() {
	return currentRefresh();
}

export function getSessionUserId() {
	try {
		return isTokenValid() ? getUserId() : null;
	} catch {
		return null; // an unreadable token: signed out, as far as caches go
	}
}

/**
 * Who a MODULE cache entry belongs to: `user:<id>` for a session, or — for a
 * token that does not parse as one (a test's, a malformed one) — the raw
 * token, so two different credentials never share an entry. A token refresh
 * for the same account keeps the id, so it keeps the entry. Stamp entries
 * with it and serve one only to the key it was stamped with: that also
 * covers a change the caches have not been told of yet (onSessionChange
 * below clears the rest).
 */
export function getSessionKey() {
	const id = getSessionUserId();
	if (id) return `user:${id}`;
	return `token:${currentToken() || ''}`;
}

const listeners = new Set();
// Seeded at load (signed out: nothing is held before hydration), so the
// account the boot finds, the first account to sign in after a sign-OUT, or
// a module cache filled before any component asked, all count as a change.
let lastSeen = getSessionUserId();

/**
 * Compares the current id with the last one seen and, on a change, tells
 * every listener (module caches and mounted `useSessionUserId`s). Safe to
 * call often; it does nothing when nothing changed.
 */
export function checkSession() {
	const now = getSessionUserId();
	if (now === lastSeen) return false;
	const previous = lastSeen;
	lastSeen = now;
	[...listeners].forEach(listener => {
		try { listener(now, previous); } catch { /* one cache must not stop the rest */ }
	});
	return true;
}

/** Subscribe to "a different account (or none) is signed in"; returns the unsubscribe. */
export function onSessionChange(listener) {
	listeners.add(listener);
	return () => listeners.delete(listener);
}

// Another tab signing in or out (the web's `storage` event; the app has no
// other tab): utils/sessionToken.js has already taken its value, the newest
// there is. Announce it.
onTokenChangedElsewhere(() => checkSession());

let hydration = null;

// One read of the store; a store that throws or refuses answers null.
function readStored(read) {
	let asked;
	try { asked = Promise.resolve(read()); } catch (error) { asked = Promise.reject(error); }
	return asked.catch(() => null);
}

const readKept = () => Promise.all([
	readStored(() => secureStore.get(TOKEN_KEY)),
	readStored(() => secureStore.get(REFRESH_KEY)),
]);

/**
 * The api client's 409 `refresh_raced` path (KPR-1674): what the store holds
 * now, `{ token, refreshToken }`. A refresh that lost a race leaves the
 * winner's pair in the store; the loser retries only with that, never with
 * the credential it sent (knowledge rule device-refresh-strict-ruling).
 */
export async function readKeptSession() {
	const [token, refreshToken] = await readKept();
	return { token: token || null, refreshToken: refreshToken || null };
}

/**
 * Read the stored token ONCE and hold it: the boot's step before the first
 * render (src/index.js awaits it). Resolves to the session's user id (null
 * signed out). Calling it again returns the same promise; it never rejects.
 *
 * A store that throws is a signed-out boot. One that takes longer than
 * `timeoutMs` lets the boot go on signed out; its answer, if it ever comes,
 * is still taken then — unless the session changed meanwhile.
 */
export function hydrateSession({ timeoutMs = HYDRATE_TIMEOUT_MS } = {}) {
	if (hydration) return hydration;
	// Hydration takes the stored value only if nothing changed while it was
	// reading: a sign-in, sign-out or another tab's change is newer than what
	// the store answered.
	const before = tokenChanges();
	const read = readKept()
		.then(([stored, storedRefresh]) => {
			if (tokenChanges() === before) restoreToken(stored, storedRefresh);
			checkSession();
			return getSessionUserId();
		});
	let timer = null;
	const bound = new Promise(resolve => {
		timer = setTimeout(() => {
			checkSession();
			resolve(getSessionUserId());
		}, timeoutMs);
	});
	hydration = Promise.race([read, bound]).finally(() => clearTimeout(timer));
	return hydration;
}

// The writes to the store not yet answered, in the order they were made.
let lastWrite = Promise.resolve(true);

function keep(write) {
	let answer;
	try { answer = Promise.resolve(write()).then(result => result !== false, () => false); } catch { answer = Promise.resolve(false); }
	lastWrite = Promise.all([lastWrite, answer]).then(([earlier, now]) => earlier && now);
	return answer;
}

/**
 * Resolves once every write made so far has reached the secure store: true
 * when all were kept, false when the store refused one (storage blocked; the
 * session then lasts only as long as the page). For a caller that must know
 * the session is kept before it leaves the page (KPR-1674's refresh; the
 * app's Keychain answers some time after it is asked). Nothing today waits:
 * every sign-in and sign-out navigates within the page.
 */
export function whenSessionKept() {
	return lastWrite;
}

/**
 * Hold a freshly issued token, write it through to the secure store, and
 * announce the change (if the account changed). The memory changes first, so
 * the next request and the next render carry it whether or not the store
 * could keep it. Returns nothing, as before: `whenSessionKept()` says when it
 * is kept.
 *
 * `refreshToken` is a device session's refresh credential (the phone app's
 * sign-in, its refresh and its password change answer one, KPR-1674). A token
 * set without one removes any credential kept before: a refresh credential
 * never outlives the session it was issued with.
 */
export function setSessionToken(token, { refreshToken = null } = {}) {
	holdToken(token, refreshToken);
	const kept = currentRefresh();
	keep(() => secureStore.set(TOKEN_KEY, token));
	keep(() => (kept ? secureStore.set(REFRESH_KEY, kept) : secureStore.remove(REFRESH_KEY)));
	checkSession();
}

/** Forget the token and any refresh credential (sign-out, account deletion), remove them from the store, and announce it. */
export function clearSessionToken() {
	holdToken(null);
	keep(() => secureStore.remove(TOKEN_KEY));
	keep(() => secureStore.remove(REFRESH_KEY));
	checkSession();
}

/** Tests only: forget the boot's read, so a test can boot again. */
export function _resetHydration() {
	hydration = null;
}

const subscribe = callback => onSessionChange(() => callback());

/**
 * The signed-in account's id, or null. Re-read on every render, so a token
 * that has expired since the last one reads as signed out, and announced
 * changes re-render on their own.
 */
export function useSessionUserId() {
	const id = useSyncExternalStore(subscribe, getSessionUserId, () => null);
	// A change nobody announced (a token whose `exp` passed while the page was
	// open) is first noticed by a render; this is where module caches hear it.
	useEffect(() => { checkSession(); }, [id]);
	return id;
}

/**
 * Calls `reset(next, previous)` when the signed-in account changes — sign-out,
 * sign-in, or another account signing in on this tab — and returns the
 * current id. Not called on mount: a fresh mount has nothing to reset.
 */
export function useOnSessionChange(reset) {
	const id = useSessionUserId();
	const seenRef = useRef(id);
	const resetRef = useRef(reset);
	resetRef.current = reset;
	useLayoutEffect(() => {
		if (seenRef.current === id) return;
		const previous = seenRef.current;
		seenRef.current = id;
		resetRef.current(id, previous);
	}, [id]);
	return id;
}
